<script setup lang="ts">
import type { SurveyQuestion } from '@/types/survey'
import SurveyPreview from './SurveyPreview.vue'
import { Calendar as CalendarIcon, Minus, Plus } from 'lucide-vue-next'
import dayjs from 'dayjs'
import { parseDate, getLocalTimeZone } from '@internationalized/date'
const { isSupportConnection } = useLocalStorage()

const surveyQuestion = defineModel<SurveyQuestion>('surveyQuestion')

const popChoice = () => {
  surveyQuestion.value?.choices.pop()
}

const addChoice = () => {
  surveyQuestion.value?.choices.push({ text: '' })
}

const launchCalendarDate = computed({
  get: () =>
    surveyQuestion.value?.launchDate
      ? parseDate(dayjs(surveyQuestion.value.launchDate).format('YYYY-MM-DD'))
      : undefined,
  set: (v) => {
    if (surveyQuestion.value) {
      surveyQuestion.value.launchDate = v ? v.toDate(getLocalTimeZone()) : null
    }
  }
})

const endCalendarDate = computed({
  get: () =>
    surveyQuestion.value?.endDate ? parseDate(dayjs(surveyQuestion.value.endDate).format('YYYY-MM-DD')) : undefined,
  set: (v) => {
    if (surveyQuestion.value) {
      surveyQuestion.value.endDate = v ? v.toDate(getLocalTimeZone()) : null
    }
  }
})

const launchMinDate = computed(() => parseDate(dayjs(new Date()).format('YYYY-MM-DD')))

const endMinDate = computed(() =>
  surveyQuestion.value?.launchDate
    ? parseDate(dayjs(surveyQuestion.value.launchDate).format('YYYY-MM-DD'))
    : parseDate(dayjs(new Date()).format('YYYY-MM-DD'))
)
</script>
<template>
  <form class="px-6 pb-6 sm:px-12 sm:pb-12">
    <div class="mt-5 xl:grid xl:grid-cols-2 xl:gap-6">
      <div class="mb-4 md:col-span-1 lg:mb-0">
        <div v-if="isSupportConnection" class="mb-4 border bg-white px-4 py-5 sm:rounded-lg sm:p-6">
          <fieldset>
            <legend class="text-base font-medium text-foreground">
              {{ $t('pages.survey.surveyCreatePage.cards.name') }}
            </legend>
            <div class="mt-4">
              <AppFormInput id="name" v-model="surveyQuestion.name" name="name" />
            </div>
          </fieldset>
        </div>
        <div class="border bg-white px-4 py-5 sm:rounded-lg sm:p-6">
          <fieldset>
            <legend class="text-base font-medium text-foreground">
              {{ $t('pages.survey.surveyCreatePage.cards.question') }}
            </legend>
            <div class="mt-4">
              <AppFormInput id="question" v-model="surveyQuestion.questionText" required name="question" />
            </div>
          </fieldset>
        </div>

        <div class="mt-4 border bg-white px-4 py-5 sm:rounded-lg sm:p-6">
          <fieldset>
            <legend class="text-base font-medium text-foreground">
              {{ $t('pages.survey.surveyCreatePage.cards.answers') }}
            </legend>
            <div class="text-sm text-muted-foreground">
              {{ $t('pages.survey.surveyCreatePage.cards.answersDescription') }}
            </div>
            <div v-for="i in 5" :key="i" class="mt-4">
              <AppFormInput
                v-if="surveyQuestion.choices.length >= i"
                :id="`answers-${i}`"
                v-model="surveyQuestion.choices[i - 1].text"
                :label="`${$t('pages.survey.surveyCreatePage.cards.answer')} #${i}`"
                name="answers"
              />
            </div>

            <div class="m-auto flex w-full flex-col items-center justify-center gap-2 py-6 md:flex-row 2xl:gap-6">
              <div class="mb-4 ml-0 w-auto md:mb-0">
                <Button
                  type="button"
                  :disabled="surveyQuestion.choices.length <= 2"
                  variant="outline"
                  @click="popChoice"
                >
                  <Minus />
                  {{ $t('pages.survey.surveyCreatePage.buttons.removeLastChoice') }}
                </Button>
              </div>
              <div v-if="surveyQuestion.choices.length < 5" class="mr-0">
                <Button
                  type="button"
                  :disabled="surveyQuestion.choices.length >= 5"
                  variant="outline"
                  @click="addChoice"
                >
                  <Plus />
                  {{ $t('pages.survey.surveyCreatePage.buttons.addChoice') }}
                </Button>
              </div>
            </div>
          </fieldset>
        </div>

        <div class="mt-4 border bg-white px-4 py-5 sm:rounded-lg sm:p-6">
          <fieldset>
            <legend class="mb-4 text-base font-medium text-foreground">
              {{ $t('pages.survey.surveyCreatePage.cards.stores') }}
            </legend>
            <MultiSelectStores
              v-model="surveyQuestion.stores"
              :max-shown-items="3"
              :has-label="false"
              class="sm:w-full"
            />
          </fieldset>
        </div>

        <div class="mt-4 border bg-white px-4 py-5 sm:rounded-lg sm:p-6">
          <fieldset class="mb-4">
            <legend class="text-base font-medium text-foreground">
              {{ $t('pages.survey.surveyCreatePage.cards.date') }}
            </legend>
            <div class="mt-4 inline-flex w-full rounded-md text-base text-foreground">
              <div class="inline-flex w-full text-base text-foreground">
                <Popover>
                  <PopoverTrigger as-child>
                    <Button type="button" variant="outline" class="w-full justify-start text-left font-normal">
                      <CalendarIcon />
                      {{
                        surveyQuestion.launchDate
                          ? dayjs(surveyQuestion.launchDate).format('DD/MM/YYYY')
                          : $t('pages.marketingFlow.marketingFlowEditPage.trigger.selectDate')
                      }}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="z-[1200] w-auto p-0" align="start">
                    <Calendar v-model="launchCalendarDate" :min-value="launchMinDate" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </fieldset>
          <fieldset>
            <legend class="text-base font-medium text-foreground">
              {{ $t('pages.survey.surveyCreatePage.cards.endDate') }}
            </legend>
            <div class="mt-4 inline-flex w-full rounded-md text-base text-foreground">
              <div class="inline-flex w-full text-base text-foreground">
                <Popover>
                  <PopoverTrigger as-child>
                    <Button type="button" variant="outline" class="w-full justify-start text-left font-normal">
                      <CalendarIcon />
                      {{
                        surveyQuestion.endDate
                          ? dayjs(surveyQuestion.endDate).format('DD/MM/YYYY')
                          : $t('pages.marketingFlow.marketingFlowEditPage.trigger.selectDate')
                      }}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="z-[1200] w-auto p-0" align="start">
                    <Calendar v-model="endCalendarDate" :min-value="endMinDate" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </fieldset>
        </div>
      </div>
      <div class="mt-4 hidden h-auto border bg-white px-4 py-5 sm:rounded-lg sm:p-6 md:col-span-1 md:mt-0 xl:inline">
        <fieldset>
          <legend class="text-base font-medium text-foreground">
            {{ $t('pages.survey.surveyCreatePage.preview.title') }}
          </legend>
          <div class="mt-16 flex w-full justify-center">
            <SurveyPreview :survey-question="surveyQuestion" />
          </div>
        </fieldset>
      </div>
    </div>
  </form>
</template>
